import { z } from 'zod';
import { optionalNullableDateInput, optionalUuidString } from '../zod-helpers/index.js';
import { MAX_TRANSFER_ORDER_LINES_PER_CREATE, MAX_TRANSFER_ORDER_LINES_PER_CREATE_MESSAGE, TRANSFER_ORDER_CONSTRAINTS, } from './constraints.js';
import { transferOrderQuantitySchema } from './item-quantity.js';
import { lowercasedRequiredUuidString } from './lowercased-required-uuid-string.js';
import { TRANSFER_ORDER_WAREHOUSES_DIFFER_MESSAGE } from './warehouses-differ-message.js';
/** Empty string → null so optional selects cannot smuggle "" as a UUID override. */
const optionalNullishUuid = (invalidMessage) => optionalUuidString(invalidMessage)
    .transform((value) => (value.trim() === '' ? null : value.toLowerCase()))
    .optional()
    .nullable();
export const createTransferOrderItemSchema = z.object({
    partId: lowercasedRequiredUuidString(`${TRANSFER_ORDER_CONSTRAINTS.part.label} is required.`, 'Invalid part selection.'),
    qty: transferOrderQuantitySchema,
});
export const transferOrderInputShape = z.object({
    brandId: lowercasedRequiredUuidString(`${TRANSFER_ORDER_CONSTRAINTS.brand.label} is required.`, 'Invalid brand selection.'),
    fromLocationGroupId: lowercasedRequiredUuidString(`${TRANSFER_ORDER_CONSTRAINTS.fromLocationGroup.label} is required.`, `Invalid ${TRANSFER_ORDER_CONSTRAINTS.fromLocationGroup.label.toLowerCase()} selection.`),
    shipToLocationGroupId: lowercasedRequiredUuidString(`${TRANSFER_ORDER_CONSTRAINTS.shipToLocationGroup.label} is required.`, `Invalid ${TRANSFER_ORDER_CONSTRAINTS.shipToLocationGroup.label.toLowerCase()} selection.`),
    carrierId: lowercasedRequiredUuidString(`${TRANSFER_ORDER_CONSTRAINTS.carrier.label} is required.`, 'Invalid carrier selection.'),
    carrierServiceId: optionalNullishUuid('Invalid carrier service selection.'),
    dateScheduled: optionalNullableDateInput,
    splitOnShortPick: z.boolean(),
    ownerIsFrom: z.boolean(),
    note: z
        .string()
        .max(TRANSFER_ORDER_CONSTRAINTS.note.maxLength, `${TRANSFER_ORDER_CONSTRAINTS.note.label} must be ${TRANSFER_ORDER_CONSTRAINTS.note.maxLength} characters or fewer.`)
        .optional()
        .nullable(),
    items: z
        .array(createTransferOrderItemSchema)
        .min(1, 'At least one part to transfer is required.')
        .max(MAX_TRANSFER_ORDER_LINES_PER_CREATE, MAX_TRANSFER_ORDER_LINES_PER_CREATE_MESSAGE),
});
/** A wizard client also blocks this, but a direct API caller has no other guard against it. */
export function refineTransferOrderWarehousesDiffer(values, context) {
    if (values.fromLocationGroupId &&
        values.shipToLocationGroupId &&
        values.fromLocationGroupId === values.shipToLocationGroupId) {
        context.addIssue({
            code: 'custom',
            message: TRANSFER_ORDER_WAREHOUSES_DIFFER_MESSAGE,
            path: ['shipToLocationGroupId'],
        });
    }
}
/** The wizard merges same-part lines before submit, but a direct API caller has no other guard against it. */
export function refineTransferOrderItemsUniquePartIds(values, context) {
    const firstIndexByPartId = new Map();
    values.items.forEach((item, index) => {
        if (firstIndexByPartId.has(item.partId)) {
            context.addIssue({
                code: 'custom',
                message: `${TRANSFER_ORDER_CONSTRAINTS.part.label} is already on this transfer order.`,
                path: ['items', index, 'partId'],
            });
            return;
        }
        firstIndexByPartId.set(item.partId, index);
    });
}
export const createTransferOrderInputSchema = transferOrderInputShape.superRefine((values, context) => {
    refineTransferOrderWarehousesDiffer(values, context);
    refineTransferOrderItemsUniquePartIds(values, context);
});
//# sourceMappingURL=create-input.js.map